import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, MessageSquare, Lock } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { useSeo } from "../../lib/hooks";
import { fmtDate } from "../../lib/api";
import { Monogram, btnGold, btnOutline } from "../../components/site/ui";

export default function Home() {
  useSeo("Commercialisti a Spilimbergo (Pordenone)", "Studio Diolosa – Commercialisti a Spilimbergo (PN): consulenza fiscale, contabilità, dichiarazioni, bilanci e avvio attività per imprese, professionisti e privati della provincia di Pordenone.");
  const team = useFetch("/public/team").data || [];
  const services = useFetch("/public/services").data || [];
  const news = useFetch("/public/news?limit=3").data?.items || [];
  const pros = team.filter((t) => t.kind === "professional");
  return (
    <>
      <section className="bg-primary text-primary-foreground grain overflow-hidden">
        <div className="container-x grid lg:grid-cols-12 gap-10 py-16 sm:py-24 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(var(--accent))] font-mono-ui">Spilimbergo · Pordenone</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08]">Studio Diolosa<br /><span className="italic font-medium text-white/90">Commercialisti</span></h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-xl">Lo studio commercialista di Spilimbergo che assiste imprese, professionisti e privati negli adempimenti fiscali, contabili e societari.</p>
            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/appuntamento" className={btnGold} data-testid="hero-cta-appointment"><CalendarCheck className="h-4 w-4" aria-hidden />Richiedi un appuntamento</Link>
              <Link to="/contatti" className="tap inline-flex items-center justify-center gap-2 rounded-md border border-white/40 px-5 font-semibold hover:bg-white/10 transition-colors" data-testid="hero-cta-contact"><MessageSquare className="h-4 w-4" aria-hidden />Contatta lo studio</Link>
              <Link to="/accedi" className="tap inline-flex items-center justify-center gap-2 rounded-md bg-white text-primary px-5 font-semibold hover:bg-white/90 transition-colors" data-testid="hero-cta-portal"><Lock className="h-4 w-4" aria-hidden />Accedi all'area clienti</Link>
            </div>
          </div>
          <figure className="lg:col-span-5 hidden md:block">
            <img src="/images/studio-illustrativa.jpg" alt="Scrivania in un ufficio (immagine illustrativa)" className="w-full aspect-[4/5] object-cover rounded-md opacity-90" width="560" height="700" loading="eager" />
            <figcaption className="mt-2 text-xs text-white/60">Immagine illustrativa, non raffigura la sede dello studio.</figcaption>
          </figure>
        </div>
      </section>

      <section className="section container-x">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div><p className="eyebrow">Servizi</p><h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-2">Come possiamo aiutarti</h2></div>
          <Link to="/servizi" className="tap inline-flex items-center gap-2 font-semibold text-primary" data-testid="home-services-link">Tutti i servizi <ArrowRight className="h-4 w-4" aria-hidden /></Link>
        </div>
        <ol className="grid gap-px bg-border border md:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 6).map((s, i) => (
            <li key={s.id} className="bg-card p-6 sm:p-8 hover:bg-secondary/40 transition-colors">
              <span className="font-mono-ui text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="text-xl font-semibold mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.summary}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section bg-secondary/50 border-y">
        <div className="container-x">
          <p className="eyebrow">I professionisti</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-2 mb-10">Il team dello studio</h2>
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pros.map((t) => (
              <li key={t.id} className="card-line p-6 flex items-center gap-5" data-testid={`home-pro-${t.id}`}>
                <Monogram name={t.name} photo={t.photo_url} size="sm" />
                <div><p className="font-display text-lg sm:text-xl font-semibold">{t.title} {t.name}</p>{t.role && <p className="text-sm text-muted-foreground">{t.role}</p>}</div>
              </li>
            ))}
          </ul>
          <Link to="/lo-studio" className={`${btnOutline} mt-8`} data-testid="home-studio-link">Conosci lo studio</Link>
        </div>
      </section>

      <section className="section container-x">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div><p className="eyebrow">Aggiornamenti</p><h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-2">Ultime circolari e notizie</h2></div>
          <Link to="/news" className="tap inline-flex items-center gap-2 font-semibold text-primary">Archivio <ArrowRight className="h-4 w-4" aria-hidden /></Link>
        </div>
        {news.length === 0 ? <p className="text-muted-foreground">Nessuna pubblicazione al momento.</p> : (
          <ul className="grid gap-6 md:grid-cols-3">
            {news.map((n) => (
              <li key={n.id}><Link to={`/news/${n.slug}`} className="block card-line p-6 h-full hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]" data-testid={`home-news-${n.slug}`}>
                <p className="text-xs text-muted-foreground"><span className="eyebrow mr-2">{n.category}</span>{fmtDate(n.published_at)}</p>
                <h3 className="text-xl font-semibold mt-3 break-words">{n.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{n.excerpt}</p>
              </Link></li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
